import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Star, ArrowRight, Sparkles, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

export const RanchoCucamongaCA = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Mobile Car Detailing in Rancho Cucamonga, CA | Precision Mobile Detailing';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional mobile car detailing in Rancho Cucamonga, CA. Serving Victoria Gardens, Alta Loma, Etiwanda, and all neighborhoods. Premium auto detailing at your location.');
    }

    // Add structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Precision Mobile Detailing - Rancho Cucamonga",
      "image": "https://customer-assets.emergentagent.com/job_shine-mobile-14/artifacts/48ukm1vz_image.png",
      "@id": "",
      "url": window.location.href,
      "telephone": businessInfo.phone,
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Rancho Cucamonga",
        "addressRegion": "CA",
        "addressCountry": "US"
      },
      "areaServed": {
        "@type": "City",
        "name": "Rancho Cucamonga",
        "addressRegion": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 34.1064,
        "longitude": -117.5931
      },
      "description": "Professional mobile car detailing service in Rancho Cucamonga, CA",
      "serviceType": ["Auto Detailing", "Car Detailing", "Mobile Detailing"]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    { name: 'Full Detail Package', link: '/services' },
    { name: 'Mini Detail', link: '/services' },
    { name: 'Essential Detail', link: '/services' },
    { name: 'Clay and Seal', link: '/services' }
  ];

  const faqs = [
    {
      question: 'Do you provide mobile detailing near Victoria Gardens in Rancho Cucamonga?',
      answer: 'Yes! We serve all of Rancho Cucamonga including the Victoria Gardens area, Haven Avenue corridor, and surrounding shopping districts. We bring our mobile detailing service directly to you, whether at home or work.'
    },
    {
      question: 'Can you detail cars in Alta Loma and Etiwanda?',
      answer: 'Absolutely. We provide full mobile car detailing services throughout Alta Loma, Etiwanda, Terra Vista, and all Rancho Cucamonga neighborhoods. Our service area covers the entire city and surrounding communities.'
    },
    {
      question: 'How does mobile car detailing work in Rancho Cucamonga?',
      answer: 'We come to your location with all necessary equipment, water, and professional products. Simply book online or by phone, and we will arrive at your Rancho Cucamonga address ready to detail your vehicle while you go about your day.'
    },
    {
      question: 'What is the cost of mobile detailing in Rancho Cucamonga, CA?',
      answer: 'Our pricing varies based on the service package and vehicle size. We offer competitive rates for the Rancho Cucamonga area with packages starting for sedans and scaling up for larger SUVs and trucks. View our Services page for detailed pricing.'
    },
    {
      question: 'Do I need to provide anything for mobile detailing?',
      answer: 'We bring everything needed for a complete detail. All we need from you is a safe place to park near your vehicle. Water and all supplies are provided by us.'
    },
    {
      question: 'How long does a mobile detail take in Rancho Cucamonga?',
      answer: 'Most detailing services take 2 to 6 hours depending on the package selected and vehicle condition. We work efficiently while maintaining the highest standards of quality.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="absolute inset-0 bg-[url('https://s3-media0.fl.yelpcdn.com/bphoto/w37ny2tARRZ4ODGq3div3g/348s.jpg')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">Rancho Cucamonga, California</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional Mobile Car Detailing in Rancho Cucamonga, CA
            </h1>
            
            <p className="text-xl mb-8 text-gray-300 leading-relaxed">
              Premium mobile auto detailing delivered to your home or business in Rancho Cucamonga. 
              Serving Victoria Gardens, Alta Loma, Etiwanda, and all Rancho Cucamonga neighborhoods with professional car care.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
                <Link to="/booking">
                  Book Rancho Cucamonga Detailing <ArrowRight className="ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
                <a href={`tel:${businessInfo.phone}`}>
                  <Phone className="mr-2" /> {businessInfo.phone}
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span>5.0 Rating • 30 Reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{businessInfo.daysOpen} • {businessInfo.hours}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Mobile Detailing in Rancho Cucamonga */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Mobile Detailing Rancho Cucamonga CA Residents Love</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Rancho Cucamonga drivers deserve convenient, high-quality car care without the hassle of traditional detailing shops. 
                Precision Mobile Detailing brings professional auto detailing directly to your driveway, parking lot, or workplace anywhere in Rancho Cucamonga.
              </p>
              <p>
                Whether you live near the bustling Victoria Gardens shopping center, in the residential communities of Alta Loma and Etiwanda, 
                or along the Haven Avenue corridor, our mobile service eliminates the need to schedule your day around a shop visit. 
                We come to you with everything needed for a complete, showroom-quality detail.
              </p>
              <p>
                From Terra Vista to Day Creek, our fully equipped mobile units serve every corner of Rancho Cucamonga. 
                We bring professional-grade products, equipment, water supply, and years of detailing expertise right to your location. 
                The result? A vehicle that looks and feels brand new, and you never had to leave home or work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">How Mobile Detailing Works in Rancho Cucamonga</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-bold mb-3">Book Your Service</h3>
                <p className="text-gray-600">Schedule online or call us. Choose your detailing package and preferred time. Flexible appointments available throughout Rancho Cucamonga.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-bold mb-3">We Arrive Fully Equipped</h3>
                <p className="text-gray-600">Our team comes to your Rancho Cucamonga location with all equipment, water, and premium detailing products. You do not need to provide anything.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-bold mb-3">Enjoy a Pristine Vehicle</h3>
                <p className="text-gray-600">Relax while we work our magic. When finished, your car looks incredible and you saved hours by not visiting a shop.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services in Rancho Cucamonga */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Car Detailing Services in Rancho Cucamonga</h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              From quick maintenance washes to full interior and exterior transformations, we offer mobile detailing packages for every need and budget.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {services.map((service, idx) => (
                <Card key={idx} className="border hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                        <Link to={service.link} className="text-black font-semibold hover:underline">
                          View Details →
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button asChild size="lg" className="cta-button text-lg px-8 py-6">
                <Link to="/services">
                  View All Services & Pricing
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why Rancho Cucamonga Chooses Precision Mobile Detailing</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Premium Quality</h3>
                  <p className="text-gray-600">We use only professional-grade detailing products and techniques to deliver showroom-quality results every time.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Trusted Service</h3>
                  <p className="text-gray-600">5.0-star rating from satisfied customers across the Inland Empire. Reliable, professional, and detail-obsessed.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">True Mobile Service</h3>
                  <p className="text-gray-600">We come to your home, office, or anywhere in Rancho Cucamonga. No waiting rooms, no wasted time driving.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area within Rancho Cucamonga */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Neighborhoods We Serve in Rancho Cucamonga</h2>
            <p className="text-center text-gray-600 mb-8 text-lg">
              Professional mobile car detailing throughout Rancho Cucamonga and surrounding communities.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {['Victoria Gardens', 'Alta Loma', 'Etiwanda', 'Terra Vista', 'Day Creek', 'Haven Avenue Corridor'].map((area, idx) => (
                <span key={idx} className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700">
                  {area}
                </span>
              ))}
            </div>

            <p className="text-center text-gray-600 mt-8">
              We also serve nearby <Link to="/service-areas/riverside-ca" className="text-black font-semibold hover:underline">Riverside</Link>, <Link to="/service-areas/san-bernardino-ca" className="text-black font-semibold hover:underline">San Bernardino</Link>, and <Link to="/service-areas/redlands-ca" className="text-black font-semibold hover:underline">Redlands</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Book Mobile Detailing in Rancho Cucamonga Today</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-300">
            Experience professional car detailing at your doorstep. Schedule your appointment now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/booking">
                Get a Quote <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
              <Link to="/service-areas">
                View All Service Areas
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RanchoCucamongaCA;
